import { ExpandMore } from "@mui/icons-material"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Button,
    Checkbox,
    DialogActions,
    FormControl,
    FormControlLabel,
    Grid2 as Grid,
    TextField,
    Typography,
} from "@mui/material"
import { grey } from "@mui/material/colors"
import { addDays, differenceInSeconds, isAfter } from "date-fns"
import moment from "moment"
import { useEffect } from "react"
import { useMissions } from "../../hooks/useMissions"
import { CollectionEventCreator, CollectionEventErrorValues } from "../../models/CollectionEventCreator"
import { FlightlineEvent } from "../../models/FlightlineEvent"
import { FlightlineStatusOption, operatorLineStatusOptions } from "../../store/types/MissionsTypes"
import { isEqual } from "../../tools"
import {
    isAutocompletePlannedLineOptionDisabled,
    unplannedLineLabel,
    unplannedLineValue,
} from "../../utils/FlightlineEventUtils"
import { TimeUtilsDateFns } from "../../utils/TimeUtils"
import {
    defaultErrorValues,
    initialCollectionEventCreation,
    useCollectionEventEditorStore,
} from "../../zstore/CollectionEventEditorStore"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { NexAutocomplete } from "../Fields/Autocomplete"
import { TimeField } from "../Fields/TimeField"
import { useStyles } from "./styles"

const requiredValues: { key: keyof CollectionEventErrorValues; label: string }[] = [
    { key: "status", label: "Status" },
    { key: "start_time", label: "Start Time" },
    { key: "stop_time", label: "Stop Time" },
    { key: "fe_planned_line_ids", label: "Planned Lines" },
]

/**
 * Calculate the duration between start and stop time
 * accounting for time crossing midnight and when flight was flown
 */
const calculateDuration = (startTime: string, stopTime: string, crossesMidnight: boolean, flownDate?: string) => {
    // if there is no flownDate then create a temporary date (this won't be saved but will help make sure the times are linear)
    // and makes sure duration is properly created
    const date = flownDate ? TimeUtilsDateFns.getDateFromFormattedDay(flownDate) : new Date()

    const startDate = TimeUtilsDateFns.addTimeToDate(date, startTime)
    let stopDate = TimeUtilsDateFns.addTimeToDate(date, stopTime)

    // Make stopDate the next day so it can properly assess if after startDate
    if (stopDate && crossesMidnight) {
        stopDate = addDays(stopDate, 1)
    }

    let duration = 0
    if (isAfter(stopDate, startDate)) {
        // stopDate is after startDate so calculate the duration between
        duration = differenceInSeconds(stopDate, startDate)
    }

    return duration
}

export const CollectionEventEditor = () => {
    const { classes, cx } = useStyles()
    const { missions } = useMissions()

    const flightlineEvents = useFlightlineEventStore((state) => state.flightlineEvents)
    const updateFlightlineEvents = useFlightlineEventStore((state) => state.updateFlightlineEvents)
    const eventPlannedLines = useFlightlineEventStore((state) => state.eventPlannedLines)

    const values = useCollectionEventEditorStore((state) => state.values)
    const updateValues = useCollectionEventEditorStore((state) => state.updateValues)
    const errors = useCollectionEventEditorStore((state) => state.errors)
    const setErrors = useCollectionEventEditorStore((state) => state.setErrors)
    const crossesMidnight = useCollectionEventEditorStore((state) => state.crossesMidnight)
    const setCrossesMidnight = useCollectionEventEditorStore((state) => state.setCrossesMidnight)
    const formIsValid = useCollectionEventEditorStore((state) => state.formIsValid)
    const setCloseEventCreator = useCollectionEventEditorStore((state) => state.setCloseEventCreator)

    const curEventPlannedLabels = eventPlannedLines.filter((opt) =>
        values.fe_planned_line_ids.find((pl_id) => pl_id === opt.plannedLineId),
    )
    const curFlightPlanFromPlannedLines: number | undefined = curEventPlannedLabels[0]?.flightPlanId

    const handleInputUpdate = <T extends keyof CollectionEventCreator>(key: T, value: CollectionEventCreator[T]) => {
        updateValues({ ...values, [key]: value })
    }

    const handleFlightlineCreate = async () => {
        // this function will be triggered by the submit event
        if (formIsValid()) {
            // update flightlineEvents obj in state
            const {
                id,
                status,
                start_time,
                stop_time,
                duration,
                notes,
                flown_agl,
                line_original_name,
                coll_event_name,
                fe_planned_line_ids,
            } = values

            const mission_id = missions.selectedMissionId
            const flown_date = missions.selectedMission.flown_date

            let flownEndMoment = moment.utc(`${flown_date} ${stop_time}`)
            if (crossesMidnight) {
                flownEndMoment = flownEndMoment.add(1, "day")
            }

            const index = flightlineEvents.findIndex((fl) => fl.fe_event_id === id)
            const oldValues: FlightlineEvent | undefined = id && index !== -1 ? flightlineEvents[index] : undefined

            const insertedNumLines = flightlineEvents.length
            const newFlightLine_event: FlightlineEvent = {
                fe_event_id: id ?? insertedNumLines + 1,
                line_original_name,
                coll_event_name,
                // TODO - how do we generate a distance from multiple planned lines?
                flight_line_distance: oldValues?.flight_line_distance ?? 0,
                flight_line_duration: Number(duration),
                flight_line_start_time: moment.utc(`${flown_date} ${start_time}`).toISOString(),
                flight_line_stop_time: flownEndMoment.toISOString(),
                flight_status: status as FlightlineStatusOption,
                flown_agl: Number(flown_agl),
                mission_id: mission_id,
                notes: notes,
                fe_planned_line_ids,
            }

            // update state missions
            // if from edit will contain "id"
            const newEvents = flightlineEvents.slice()
            if (id) {
                newEvents[index] = {
                    ...newEvents[index],
                    ...newFlightLine_event,
                }
            } else {
                newEvents.unshift(newFlightLine_event)
            }

            updateFlightlineEvents(newEvents)
            setCloseEventCreator()
        }
    }

    // Update duration
    // duration is reliant on start and stop times and on crossesMidnight boolean
    useEffect(() => {
        const startTime = values.start_time
        const stopTime = values.stop_time

        const duration = calculateDuration(
            startTime,
            stopTime,
            crossesMidnight,
            missions.selectedMission.flown_date ?? undefined,
        )

        updateValues({ ...values, duration: duration.toString() })
    }, [values.start_time, values.stop_time, crossesMidnight])

    // Error handling
    // this function will check if the form values are valid
    useEffect(() => {
        const newErrors: CollectionEventErrorValues = { ...defaultErrorValues }
        if (!isEqual(values, initialCollectionEventCreation)) {
            requiredValues.forEach((req) => {
                newErrors[req.key] = !values[req.key] ? `${req.label} is required.` : ""
            })

            // duration cannot be positive without stop time being after start time
            // stop_time validation after start_time calculated during duration update
            if (Number(values.duration) <= 0 || Number.isNaN(+values.duration)) {
                newErrors.duration = "Stop time must be after the start time."
            }

            // fe_planned_line_id validation
            const plannedLineIds = values.fe_planned_line_ids.map((id) => Number(id))
            if (plannedLineIds.includes(unplannedLineValue) && values.status !== "Ignored") {
                newErrors.status = `(${unplannedLineLabel}) Unplanned line status must be set as Ignored.`
            }
        }
        setErrors({ ...newErrors })
    }, [values, crossesMidnight])

    return (
        <Grid container direction={"column"} key="new-collection-event-form-wrapper" className={cx(classes.input__md)}>
            <FormControl key="new-collection-event-form-control">
                <TextField
                    name="line_original_name"
                    label="Original Name"
                    value={values.line_original_name}
                    variant="outlined"
                    className={cx(classes.gutterBottom__md)}
                    onChange={(e) => handleInputUpdate("line_original_name", e.target.value)}
                    key="new-flightline-event-original-name"
                />
                <NexAutocomplete
                    multiple={true}
                    value={curEventPlannedLabels}
                    options={eventPlannedLines}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(opt, val) => opt.plannedLineId === val.plannedLineId}
                    getOptionDisabled={(opt) =>
                        isAutocompletePlannedLineOptionDisabled(
                            opt,
                            values.fe_planned_line_ids,
                            curFlightPlanFromPlannedLines,
                        )
                    }
                    onChange={(_, tableOption) => {
                        const eventPlannedLines = tableOption.map((obj) => obj.plannedLineId)
                        handleInputUpdate("fe_planned_line_ids", eventPlannedLines)
                    }}
                    sx={{ minWidth: 260, pb: 2 }}
                    limitTags={1}
                    TextFieldProps={{
                        label: "Flight Plan - Name",
                        required: true,
                        error: curEventPlannedLabels.length === 0,
                    }}
                />
                <TextField
                    name="coll_event_name"
                    label="Sensor Label"
                    key="new-coll-event-name"
                    variant="outlined"
                    className={cx(classes.gutterBottom__md, classes.input__md)}
                    onChange={(e) => handleInputUpdate("coll_event_name", e.target.value)}
                />
                <NexAutocomplete
                    id="status"
                    options={operatorLineStatusOptions}
                    value={values.status ?? null}
                    key="new-collection-event-status"
                    className={cx(classes.gutterBottom__md)}
                    onChange={(_, value) => handleInputUpdate("status", value ?? "")}
                    TextFieldProps={{ label: "Status", error: errors.status !== "" }}
                />
                {values.sensor_details && (
                    <Grid key="collection-event-sensor-details" sx={{ pb: 4 }}>
                        <Accordion variant="outlined">
                            <AccordionSummary expandIcon={<ExpandMore />}>Sensor Details</AccordionSummary>
                            <AccordionDetails sx={{ backgroundColor: grey[100] }}>
                                <Typography color={grey[700]} sx={{ whiteSpace: "pre-line" }}>
                                    {values.sensor_details}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                )}
                <Grid key="new-notes-wrapper" sx={{ pb: 4 }}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={"Notes"}
                        value={values.notes}
                        onChange={(e) => handleInputUpdate("notes", e.target.value)}
                    />
                </Grid>
                <Grid container key="time-container" columnSpacing={2} sx={{ pb: 4 }}>
                    <Grid size={{ xs: 6 }} key="start-time-subcontainer">
                        <TimeField
                            fullWidth
                            required={true}
                            label="Start Time"
                            key="new-collection-event-start-time"
                            field="start_time"
                            slotProps={{
                                htmlInput: {
                                    step: 1, // enable hh:mm:ss display
                                },
                            }}
                            onChange={(_, value) => handleInputUpdate("start_time", value)}
                            value={values.start_time ?? ""}
                            error={!!errors.start_time}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }} key="stop-time-subcontainer">
                        <TimeField
                            fullWidth
                            required={true}
                            label="Stop Time"
                            key="new-collection-event-stop-time"
                            field="stop_time"
                            slotProps={{
                                htmlInput: {
                                    step: 1, // enable hh:mm:ss display
                                },
                            }}
                            onChange={(_, value) => handleInputUpdate("stop_time", value)}
                            value={values.stop_time ?? ""}
                            error={!!errors.stop_time || Number(values.duration) <= 0 || Number.isNaN(+values.duration)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                            labelPlacement="start"
                            label={"Flight Crosses Midnight"}
                            sx={{ ml: 0 }}
                            control={
                                <Checkbox
                                    size="small"
                                    onChange={(e) => setCrossesMidnight(e.target.checked)}
                                    checked={crossesMidnight}
                                />
                            }
                        />
                    </Grid>
                </Grid>
                <TextField
                    name="new-collection-event_duration"
                    key="collection-event-duration"
                    label="Duration (s)"
                    value={values.duration}
                    disabled
                    variant="outlined"
                    className={cx(classes.gutterBottom__md, classes.input__md)}
                />
                <TextField
                    name="flown_agl"
                    label="FlightLine AGL (m)"
                    key="new-collection-event-agl"
                    variant="outlined"
                    value={values.flown_agl}
                    className={cx(classes.gutterBottom__md, classes.input__md)}
                    onChange={(e) => handleInputUpdate("flown_agl", e.target.value)}
                />
            </FormControl>
            <Grid>
                {!formIsValid() && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {Object.values(errors)
                            .filter((e) => e !== "")
                            .map((msg, idx) => (
                                <li style={{ width: "100%" }} key={idx}>
                                    {msg}
                                </li>
                            ))}
                    </Alert>
                )}
            </Grid>
            <DialogActions>
                <Button
                    color="info"
                    size="large"
                    onClick={() => setCloseEventCreator()}
                    variant="outlined"
                    key="new-button-cancel"
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    size="large"
                    onClick={handleFlightlineCreate}
                    variant="contained"
                    key="new-button-update"
                    disabled={
                        !formIsValid() ||
                        !(values.start_time.length > 0) ||
                        !(values.stop_time.length > 0) ||
                        values.fe_planned_line_ids.length === 0
                    }
                >
                    Update Events Table
                </Button>
            </DialogActions>
        </Grid>
    )
}
